import { Component, EventEmitter, Input, OnChanges, Output, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { AgGridTableWrapper } from '../../utils';
import { ColAction, ColActionId, FolderField, IoniconsOutline, ListData, TableActionData } from '@pt/pt-models';
import {
  CustomRendererComponent,
  DateFormatterRendererComponent,
  TranslateServiceRendererComponent
} from '../../ag-grid';
import { WorkspaceItem } from '@primatoo-ms-organization';
import { PermissionManager } from '@pt/pt-core';

@Component({
  selector: 'pt-attachment-workspace-table',
  template: `
    <ag-grid-angular
      *ngIf='gridOptions()'
      [statusBar]="statusBar"
      (filterChanged)="onFilterChanged()"
      [rowStyle]=''
      class='ag-theme-balham ag-theme-balham--workspace w-100 h-100'
      [loadingOverlayComponent]="loadingOverlayComponent"
      [loadingOverlayComponentParams]="loadingOverlayComponentParams"
      [rowData]='rowData'
      [gridOptions]='gridOptions()'
      (gridReady)='onGridReady($event)'
      (rowDoubleClicked)='doubleClickRow($event)'
      (cellClicked)='onClickCell($event)'>
    </ag-grid-angular>

    <ng-template #nameCol let-node>
      <ng-container *ngIf='node.row.type === "ATTACHMENT"'>
        <pt-image-renderer [type]='node.row.type'
                           [label]='node.row.name'>
          <div additionalContent>
            <pt-icon *ngIf='node.row?.builtIn'
                     [key]='IconClosedLock'
                     class='d-flex' container='body'
                     ngbTooltip="{{'LABELS.BUILT_IN' | translate}}"
            ></pt-icon>
          </div>
        </pt-image-renderer>
      </ng-container>

      <ng-container *ngIf='node.row.type !== "ATTACHMENT"'>
        <pt-image-renderer [label]='node.row.name'
                           [type]='node.row.type'>
          <div additionalContent>
            <pt-icon *ngIf='node.row?.builtIn'
                     [key]='IconClosedLock'
                     class='d-flex' container='body'
                     ngbTooltip="{{'LABELS.BUILT_IN' | translate}}"
            ></pt-icon>
          </div>
        </pt-image-renderer>
      </ng-container>
    </ng-template>
  `
})
export class AttachmentsWorkspaceTableComponent extends AgGridTableWrapper<WorkspaceItem> implements OnChanges {

  @Input() data: ListData<WorkspaceItem> = null;
  @Output() clickAction: EventEmitter<TableActionData<WorkspaceItem>> = new EventEmitter<TableActionData<WorkspaceItem>>();

  @ViewChild('nameCol', { static: true }) nameCol: TemplateRef<unknown>;

  public rowData: WorkspaceItem[];
  public IconClosedLock = IoniconsOutline.lock;

  constructor(public translate: TranslateService, public permissionManager: PermissionManager) {
    super(translate, permissionManager);
  }

  ngOnChanges(): void {
    const folderData: ListData<WorkspaceItem> | null = this.loadData();
    this.onChangeData(folderData);

    this.rowData = folderData.data;
  }

  loadData(): ListData<WorkspaceItem> {
    if (this.data) {
      return this.data;
    }
    return null;
  }

  doubleClickRow(params) {
    this.clickAction.emit({ action: { id: ColActionId.showDetail }, row: params.data });
  }

  clickActions(action: ColAction, row: WorkspaceItem) {
    // EMPTY
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }

  createColumnDefs(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      field: FolderField.name,
      cellClass: 'workspace-cell',
      flex: 1,
      resizable: true,
      sortable: true,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      cellRenderer: CustomRendererComponent,
      cellRendererParams: {
        ngTemplate: this.nameCol
      }
    } as ColDef);

    colDef.push({
      field: FolderField.createdAt,
      cellClass: 'workspace-cell',
      width: 170,
      flex: 1,
      cellRenderer: 'dateFormatterRenderer',
      resizable: true,
      filter: true
    } as ColDef);

    return colDef;
  }

  createGridOptions(columnDefs: ColDef[], rowData: WorkspaceItem[]): GridOptions {
    return {
      animateRows: false,
      pagination: false,
      rowSelection: 'single',
      rowHeight: 30,
      floatingFiltersHeight: 30,
      defaultColDef: {
        cellStyle: { 'line-height': '30px', 'font-size': '12px' }
      },
      columnDefs,
      components: {
        customRendererComponent: CustomRendererComponent,
        dateFormatterRenderer: DateFormatterRendererComponent,
        translateServiceRenderer: TranslateServiceRendererComponent
      }
    } as GridOptions;
  }

  colActionsTemplate(): TemplateRef<unknown> {
    return null;
  }

  onClickCell($event: any) {
    const row: WorkspaceItem = $event.data;
    if (row.type !== 'FOLDER') {
      this.clickAction.emit({ action: { id: ColActionId.showDetail }, row: $event.data });
    } else {
      this.clickAction.emit({ action: { id: ColActionId.select }, row: $event.data });
    }
  }
}
